import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { useSnapshotThumbnails } from "../../application/useSnapshots";
import { BoardSnapshot } from "../../domain/types";

type Props = {
    sessionId: string;
    snapshots: BoardSnapshot[];
    onRename: (index: number, name: string) => Promise<void> | void;
    onDelete: (index: number) => Promise<void> | void;
};

/**
 * Therapist-side gallery of the captures taken on the live board. Streams each
 * thumbnail (authenticated, by index) and lets the therapist rename or delete a
 * capture. Renders nothing until there is at least one snapshot.
 */
export default function ConstellationSnapshotManager({
    sessionId,
    snapshots,
    onRename,
    onDelete,
}: Props) {
    const { t } = useTranslation();
    const [active, setActive] = useState<string | null>(null);
    const [editing, setEditing] = useState<number | null>(null);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);

    // Re-stream thumbnails whenever the set of captures changes (add/delete).
    const thumbs = useSnapshotThumbnails(sessionId, snapshots.map((s) => s.url));

    if (snapshots.length === 0) return null;

    const startEdit = (i: number) => {
        setEditing(i);
        setDraft(snapshots[i]?.name ?? "");
    };

    const saveEdit = async (i: number) => {
        setBusy(true);
        try {
            await onRename(i, draft.trim());
            setEditing(null);
        } finally {
            setBusy(false);
        }
    };

    const remove = async (i: number) => {
        if (!window.confirm(t("constellation.therapist.snapshotDeleteConfirm"))) return;
        setBusy(true);
        try {
            await onDelete(i);
            if (editing === i) setEditing(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <h2 className="text-sm font-medium">
                {t("constellation.therapist.snapshotsTitle")}
            </h2>
            <ul className="mt-2 flex flex-col gap-2">
                {snapshots.map((snap, i) => (
                    <li
                        key={`${snap.url}-${i}`}
                        className="flex items-center gap-2 rounded-lg border border-gray-200 p-2"
                    >
                        <button
                            type="button"
                            onClick={() => thumbs[i] && setActive(thumbs[i])}
                            className="h-12 w-12 shrink-0 overflow-hidden rounded-md border bg-gray-50"
                            aria-label={t("constellation.snapshots.alt", "Constellation snapshot {{n}}", {
                                n: i + 1,
                            })}
                        >
                            {thumbs[i] ? (
                                <img
                                    src={thumbs[i] as string}
                                    alt=""
                                    aria-hidden
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <span className="block h-full w-full animate-pulse bg-gray-100" />
                            )}
                        </button>

                        {editing === i ? (
                            <>
                                <input
                                    autoFocus
                                    value={draft}
                                    maxLength={80}
                                    onChange={(e) => setDraft(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") saveEdit(i);
                                        if (e.key === "Escape") setEditing(null);
                                    }}
                                    placeholder={t("constellation.therapist.snapshotNamePlaceholder")}
                                    className="min-w-0 flex-1 rounded-md border px-2 py-1 text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={() => saveEdit(i)}
                                    disabled={busy}
                                    aria-label={t("common.save", "Save")}
                                    className="rounded p-1.5 text-green-600 hover:bg-green-50 disabled:opacity-50"
                                >
                                    <Check className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEditing(null)}
                                    aria-label={t("common.cancel", "Cancel")}
                                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </>
                        ) : (
                            <>
                                <span
                                    className="min-w-0 flex-1 truncate text-sm text-gray-800"
                                    title={snap.name}
                                >
                                    {snap.name || t("constellation.therapist.snapshotUnnamed", {
                                        n: i + 1,
                                    })}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => startEdit(i)}
                                    aria-label={t("constellation.therapist.snapshotRename")}
                                    title={t("constellation.therapist.snapshotRename")}
                                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-brand-600"
                                >
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => remove(i)}
                                    disabled={busy}
                                    aria-label={t("constellation.therapist.snapshotDelete")}
                                    title={t("constellation.therapist.snapshotDelete")}
                                    className="rounded p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </>
                        )}
                    </li>
                ))}
            </ul>

            {active && createPortal(
                <div
                    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setActive(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <img
                        src={active}
                        alt={t("constellation.snapshots.title", "Constellation snapshots")}
                        className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
                    />
                </div>,
                document.body,
            )}
        </div>
    );
}
